import axios from 'axios';
import { isEmpty } from 'lodash';
import * as Sentry from '@sentry/react';
import AuthService from './authService';
import secureLocalStorage from 'react-secure-storage';

const requestArray: any = [];
const authService = new AuthService();

// Instance utilities (extracted from useInstance hook logic)
const isDebug = process.env.REACT_APP_SECURE_LOCAL_STORAGE_ENABLE === 'false';
const storage = isDebug ? localStorage : secureLocalStorage;

type Tenant = {
  cid: number;
  tenantId: string;
  roleId: string;
  instanceName?: string;
  instanceId?: string;
};

const getCurrentCid = () => {
  const hash = window.location.hash;
  const cid = +hash.replace('#', '').replace('cid=', '');
  return cid;
};

const getAllTenants = () => {
  const tenants = (JSON.parse((storage.getItem('tenants') as string) || '[]') ||
    []) as unknown as Tenant[];
  return tenants;
};

const getTenantById = (cid: number = getCurrentCid()) => {
  const tenants = getAllTenants();
  return tenants.find((tenant) => tenant.cid === cid);
};

const getRoleId = (cid: number = getCurrentCid()) => {
  const tenant = getTenantById(cid);
  return tenant?.roleId;
};

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '',
});
export const cancelAxios = axios.CancelToken;

let busyApiCall = false;
let isRetrying = false;
let refreshPromise: Promise<string | null> | null = null;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

// Process the failed queue with the new token or null if refresh failed
const processQueue = (token: string | null, error: any = null) => {
  console.log(
    `[ProcessQueue] Processing ${failedQueue.length} queued requests: ${
      token ? 'with new token' : 'failed - no token'
    }`
  );

  failedQueue.forEach((request, index) => {
    if (token) {
      // Retry the request with the new token
      const retryConfig = { ...request.config };

      // Ensure headers exist
      if (!retryConfig.headers) {
        retryConfig.headers = {};
      }

      // Update the Authorization header with the new token
      retryConfig.headers.Authorization = `Bearer ${token}`;
      retryConfig.headers.bearerToken = token || '';

      // Ensure method is preserved (important for POST/PUT requests)
      if (!retryConfig.method && request.config.method) {
        retryConfig.method = request.config.method;
      }

      // Ensure data is preserved for POST/PUT requests
      if (
        (retryConfig.method === 'post' ||
          retryConfig.method === 'put' ||
          retryConfig.method === 'patch') &&
        !retryConfig.data &&
        request.config.data
      ) {
        retryConfig.data = request.config.data;
      }

      // Preserve content-type header for requests with data
      if (retryConfig.data && request.config.headers && request.config.headers['Content-Type']) {
        retryConfig.headers['Content-Type'] = request.config.headers['Content-Type'];
      }

      console.log(
        `[ProcessQueue] Retrying request ${index + 1}/${failedQueue.length} to ${
          retryConfig.url
        } (${retryConfig.method || 'GET'})`
      );

      axiosInstance(retryConfig)
        .then((response) => {
          console.log(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} succeeded after retry`
          );
          request.resolve(response);
        })
        .catch((err) => {
          console.error(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} failed after retry:`,
            err.message
          );
          request.reject(err);
        });
    } else {
      // If token refresh failed, reject all requests in queue
      console.log(
        `[ProcessQueue] Rejecting request ${index + 1}/${
          failedQueue.length
        } due to failed token refresh`
      );
      request.reject(error);
    }
  });

  // Reset the queue and refreshing state
  console.log('[ProcessQueue] Reset queue and state');
  failedQueue = [];
  isRetrying = false;
  refreshPromise = null;
};

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async function (config) {
    config.headers = {
      ...config.headers,
    };

    // Get role ID and tenant ID using instance utilities
    const roleId = getRoleId();
    const tenant = getTenantById();

    if (roleId) {
      config.headers['X-Switch-Source-Role-Id'] = roleId;
    }

    if (tenant?.tenantId) {
      config.headers['X-Tenant-Id'] = tenant.tenantId;
    }

    // Check if token is expired and refresh it before making the request
    if (authService.isTokenExpired()) {
      console.log('[Request Interceptor] Token is expired, attempting to refresh');
      try {
        const newToken = await authService.signInSilent();
        if (newToken) {
          console.log('[Request Interceptor] Token refreshed successfully');
          // Add the new token to the request
          config.headers.Authorization = `Bearer ${newToken}`;
          config.headers.bearerToken = newToken || '';
        } else {
          console.log('[Request Interceptor] Token refresh returned null');
        }
      } catch (error) {
        console.error('[Request Interceptor] Failed to refresh token:', error);
        // Continue with the request - the response interceptor will handle 401 errors
      }
    } else {
      // If token is valid, add it to the request
      const token = authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.bearerToken = token || '';
      }
    }

    return config;
  },
  async function (error: any) {
    return await Promise.reject(error);
  }
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    if (requestArray.length !== 0) {
      requestArray.forEach(function (x: any, i: any) {
        if (response.config.url === x.url) {
          requestArray.splice(i, 1);
        }
      });
    }
    return response;
  },
  async function (error: any) {
    const correlationId = error.response?.headers?.['x-error-correlation-id'];
    const extra_details = {
      data: error.config?.data,
      response: JSON.stringify(error.response, null, 2)
        ? {
            status: error.response.status,
            data: JSON.stringify(error.response.data, null, 2),
          }
        : null,
      correlationId: correlationId,
    };
    Sentry.captureException(error, {
      extra: extra_details,
    });

    // Store the original request
    const originalRequest = error.config;
    requestArray.push(originalRequest);

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log('[Response Interceptor] 401 Unauthorized error, queueing request for retry');
      const retryRequest = new Promise((resolve, reject) => {
        // Add to queue to retry after token refresh
        failedQueue.push({
          resolve,
          reject,
          config: originalRequest,
        });
      });

      // If we're not already refreshing the token, initiate refresh
      if (!isRetrying) {
        console.log('[Response Interceptor] Starting token refresh');
        isRetrying = true;

        // Create a promise for the token refresh that can be reused
        refreshPromise = new Promise<string | null>((resolve) => {
          authService
            .signInSilent()
            .then((token) => {
              // We got a new token, resolve with it
              console.log('[Response Interceptor] Token refresh successful');
              resolve(token as string);
            })
            .catch((refreshError) => {
              // Failed to get token
              console.error('[Response Interceptor] Failed to refresh token:', refreshError);
              resolve(null);
            });
        });

        // Process the token result
        refreshPromise.then((accessToken) => {
          if (accessToken) {
            console.log('[Response Interceptor] Processing retry queue with new token');
            // Process the queue with the new token
            processQueue(accessToken);
          } else {
            console.log('[Response Interceptor] No token obtained, rejecting all queued requests');
            // If we couldn't get a new token, reject all requests
            processQueue(null, error);

            // If no active API call is in progress, dispatch SIGN_IN event
            if (!busyApiCall) {
              busyApiCall = true;
              console.log('[Response Interceptor] Dispatching SIGN_IN event');
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'SIGN_IN' }));
              }, 1000);
            }
          }
        });
      } else {
        console.log('[Response Interceptor] Token refresh already in progress, request queued');
      }

      // Return the promise that will resolve/reject based on the token refresh
      return retryRequest;
    }

    if (error.response?.status === 403 && isEmpty(error?.response?.data?.error)) {
      if (!busyApiCall) {
        busyApiCall = true;
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'REFRESH' }));
        }, 1000);
      }
    }

    return await Promise.reject(error);
  }
);
